import { useEffect, useRef } from 'react';
import Konva from 'konva';
import { FieldTransform } from './FieldCanvas';

interface ReferenceImageLayerProps {
  stage: Konva.Stage;
  transform: FieldTransform;
  imageSrc: string;
  opacity: number;
  scaleX: number;  // horizontal multiplier on initial fit size; 1.0 = fitted to field bounds
  scaleY: number;  // vertical multiplier on initial fit size; 1.0 = fitted to field bounds
  locked: boolean;
}

export const ReferenceImageLayer: React.FC<ReferenceImageLayerProps> = ({
  stage, transform, imageSrc, opacity, scaleX, scaleY, locked,
}) => {
  const layerRef  = useRef<Konva.Layer | null>(null);
  const imgRef    = useRef<Konva.Image | null>(null);
  // Center of the image in stage world coords; updated on drag.
  const centerRef = useRef({ cx: 0, cy: 0 });
  // Base dimensions at scale=1 (fitted to field bounds on load).
  const baseRef   = useRef({ w: 0, h: 0 });

  // Create layer once; slot it above the field background but below grid/bunkers.
  useEffect(() => {
    const layer = new Konva.Layer();
    layer.name('refImage');
    stage.add(layer);
    layer.moveToBottom();
    layer.moveUp();
    layerRef.current = layer;
    return () => { layer.destroy(); };
  }, [stage]);

  // Rebuild image whenever the source changes.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.destroyChildren();
    imgRef.current = null;

    const htmlImg = new window.Image();
    htmlImg.onload = () => {
      if (!layerRef.current) return;

      const { fieldOriginX: ox, fieldOriginY: oy, basePxPerFoot,
              canvasHorizFt, canvasVertFt } = transform;
      const fw = canvasHorizFt * basePxPerFoot;
      const fh = canvasVertFt  * basePxPerFoot;

      // Scale-1 dimensions: fit image inside field bounds, maintaining aspect ratio.
      const imgAspect   = htmlImg.naturalWidth / htmlImg.naturalHeight;
      const fieldAspect = fw / fh;
      const baseW = imgAspect > fieldAspect ? fw : fh * imgAspect;
      const baseH = baseW / imgAspect;
      baseRef.current = { w: baseW, h: baseH };

      // Initial center = field center.
      const cx = ox + fw / 2;
      const cy = oy + fh / 2;
      centerRef.current = { cx, cy };

      const dispW = baseW * scaleX;
      const dispH = baseH * scaleY;

      const imgNode = new Konva.Image({
        image: htmlImg,
        x: cx - dispW / 2,
        y: cy - dispH / 2,
        width: dispW,
        height: dispH,
        opacity,
        draggable: true,
      });
      imgRef.current = imgNode;
      layer.add(imgNode);

      imgNode.on('dragmove', () => {
        // Track center so scale changes keep the image centered on where the user left it.
        const w = imgNode.width();
        const h = imgNode.height();
        centerRef.current = {
          cx: imgNode.x() + w / 2,
          cy: imgNode.y() + h / 2,
        };
      });

      layer.batchDraw();
    };
    htmlImg.src = imageSrc;
  // transform and scale intentionally excluded from deps — initial placement only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc, stage]);

  // Resize from center whenever scaleX or scaleY changes.
  useEffect(() => {
    const imgNode = imgRef.current;
    const layer   = layerRef.current;
    if (!imgNode || !layer) return;
    const { w, h } = baseRef.current;
    const { cx, cy } = centerRef.current;
    const dispW = w * scaleX;
    const dispH = h * scaleY;
    imgNode.setAttrs({ x: cx - dispW / 2, y: cy - dispH / 2, width: dispW, height: dispH });
    layer.batchDraw();
  }, [scaleX, scaleY]);

  // Sync opacity without rebuilding.
  useEffect(() => {
    if (!imgRef.current || !layerRef.current) return;
    imgRef.current.opacity(opacity);
    layerRef.current.batchDraw();
  }, [opacity]);

  // Disable drag when locked (e.g. during bunker placement).
  useEffect(() => {
    imgRef.current?.draggable(!locked);
  }, [locked]);

  return null;
};
